import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import {
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import { workflowConfigService } from '../../services/workflowConfigService';
import WorkflowVisualizer from '../../components/WorkflowVisualizer';

const WorkflowConfig = () => {
  // const { workflowName } = useParams();
  const workflowName = 'ncexgratia';
  if (!workflowName) {
    return <Alert severity="error">Workflow name is required</Alert>;
  }

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['workflow', workflowName],
    queryFn: async () => {
      // try primary endpoint
      const raw = await workflowConfigService.getWorkflowByName(workflowName);
      const n = normalize(raw);
      if (n) return n;
      // fallback: list of steps endpoint
      try {
        const stepsRaw = await workflowConfigService.getWorkflowSteps(workflowName);
        // if it returns an array directly
        if (Array.isArray(stepsRaw) && stepsRaw.length) return { steps: stepsRaw };
        // if it's wrapped
        if (stepsRaw && Array.isArray(stepsRaw.steps)) return { steps: stepsRaw.steps };
      } catch (err) {
        // swallow and let outer error handling display
        console.debug('fallback getWorkflowSteps failed', err);
      }
      return null;
    },
    keepPreviousData: true,
    retry: 1,
  });

  const normalize = raw => {
    if (!raw) {
      console.warn("No workflow data provided");
      return null;
    }
    if (Array.isArray(raw.config.steps) && raw.config.steps.length) {
      return raw.config;
    }
    return null;
  };

  const visualData = data || null;

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Workflow Configuration
      </Typography>

      <Typography variant="body1" color="text.secondary" mb={3}>
        Configure and manage application workflow settings
      </Typography>

      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Workflow Management System
            </Typography>
          </Box>

          <Alert severity="info">
            This feature is under development.
          </Alert>

          {isLoading && <CircularProgress />}
          {isError && (
            <Box mt={2}>
              <Alert severity="error">Failed to load workflow configuration: {String(error?.message || error)}</Alert>
              <Box mt={1}>
                <Button variant="outlined" onClick={() => refetch()}>Retry</Button>
              </Box>
            </Box>
          )}
          {!isLoading && !visualData && !isError && (
            <Box mt={2}>
              <Alert severity="warning">No workflow configuration found for "{workflowName}". Try creating one in the backend or verify the API.</Alert>
              <Box mt={1}>
                <Button variant="outlined" onClick={() => refetch()}>Retry</Button>
              </Box>
            </Box>
          )}
          {visualData && (
            <Box mt={2}>
                <WorkflowVisualizer workflow={visualData} />
            </Box>
          )}
        </CardContent>
      </Card>

    </Box>
  );
};

export default WorkflowConfig;
